import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { useTheme } from "@mui/material";
import { tokens } from "../../../../../theme";
import HistoryIcon from "@mui/icons-material/History";

import { AuditAction, AuditEntry } from "./types/AuditTypes";
import AuditItem from "./AuditItem";

type FilterValue = "all" | AuditAction;

const filterOptions: { value: FilterValue; label: string }[] = [
  { value: "all", label: "All Activity" },
  { value: "created", label: "Created" },
  { value: "updated", label: "Updated" },
  { value: "status_change", label: "Status Changes" },
  { value: "deleted", label: "Deleted" },
  { value: "assigned", label: "Assigned" },
];

interface AuditHistoryProps {
  entries: AuditEntry[]; // required now
}

const AuditHistory = ({ entries }: AuditHistoryProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [filter, setFilter] = useState<FilterValue>("all");

  const filteredEntries =
    filter === "all"
      ? entries
      : entries.filter((entry) => entry.action === filter);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: `1px solid ${colors.primary[300]}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryIcon sx={{ fontSize: 22, color: "#374151" }} />
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "22px",
              color: "#4B5563",
            }}
          >
            Audit history
          </Typography>
        </Box>

        <FormControl size="small">
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterValue)}
            sx={{
              fontSize: "14px",
              color: "text.secondary",
              height: 36,
              width: 140,
              bgcolor: colors.primary[900], // white inside

              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#94A3B8", // border color
                borderRadius: 2,
              },

              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#94A3B8",
              },

              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#94A3B8",
                borderWidth: 1,
              },

              "& .MuiSelect-select": {
                py: 0.5,
                pr: 3,
                pl: 1.5,
              },
            }}
          >
            {filterOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{ fontSize: "0.8125rem" }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Paper
        sx={{
          borderRadius: "12px",
          boxshadow: 0,
          border: "1px solid #E5E7EB",
          overflow: "hidden",
          bgcolor: colors.primary[900],
        }}
      >
        {/* Timeline entries */}
        <Box
          sx={{
            px: 3,
            pt: 2.5,
            pb: 1,
            maxHeight: "none", // remove any limit
            overflow: "visible", // ensure all items are shown
          }}
        >
          {filteredEntries.length > 0 ? (
            <>
              {filteredEntries.map((entry, index) => (
                <AuditItem
                  key={entry.id}
                  entry={entry}
                  isLast={index === filteredEntries.length - 1}
                />
              ))}

              {/* End dot */}

              <Box sx={{ display: "flex", ml: "5px", pb: 2 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: "#F3E4DA",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "#724E41",
                    }}
                  />
                </Box>
              </Box>
            </>
          ) : (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography sx={{ color: "#9CA3AF", fontSize: "0.875rem" }}>
                No activity found for this filter.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
export default AuditHistory;
